import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import isencaoLicenciamentoConsultaService from "../services/isencaoLicenciamentoConsulta";
import { statusIsencaoEnum, statusIsencaoLabels } from "../utils/constants";

const AssinarIsencao = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isencao, setIsencao] = useState(null);
  const [certificadoPfxFile, setCertificadoPfxFile] = useState(null);
  const [certificadoSenha, setCertificadoSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await isencaoLicenciamentoConsultaService.obterPorId(id);
        setIsencao(data);
      } catch {
        setError("Erro ao carregar dados da isenção.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handlePfxChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith(".pfx")) {
        setError("Apenas arquivos .pfx são aceitos.");
        e.target.value = "";
        return;
      }
      setCertificadoPfxFile(file);
    }
  };

  const handleVisualizarIsencao = async () => {
    try {
      const data = await isencaoLicenciamentoConsultaService.baixarDocumentoEmitido(id);
      const byteArray = Uint8Array.from(atob(data.conteudo), (c) => c.charCodeAt(0));
      const blob = new Blob([byteArray], { type: data.contentType || "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch {
      setError("Erro ao visualizar a isenção. Tente novamente.");
    }
  };

  const handleAssinar = async () => {
    if (!certificadoPfxFile) {
      setError("Selecione o arquivo do certificado digital (.pfx).");
      return;
    }
    if (!certificadoSenha) {
      setError("Informe a senha do certificado.");
      return;
    }

    try {
      setSigning(true);
      setError("");
      await isencaoLicenciamentoConsultaService.assinarIsencao(
        id,
        certificadoPfxFile,
        certificadoSenha,
      );
      setSuccess(true);
      setTimeout(() => navigate("/secretario/dashboard"), 2000);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Erro ao assinar a isenção. Verifique o certificado e a senha.";
      setError(msg);
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  const jaAssinada =
    isencao?.assinadoDigitalmente ||
    isencao?.status === statusIsencaoEnum.ASSINADA;
  const temDocumentoEmitido = !!(
    isencao?.isencaoEmitida || isencao?.isencaoEmitidaS3Key
  );

  return (
    <Container maxWidth="md" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 5 } }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={2}
          sx={{ mb: 1 }}
        >
          <Typography
            variant="h5"
            component="h1"
            fontWeight="bold"
            sx={{ fontSize: { xs: "1.1rem", md: "1.5rem" } }}
          >
            Assinar Isenção Digitalmente
          </Typography>
          {temDocumentoEmitido && (
            <Button
              variant="outlined"
              size="small"
              fullWidth
              startIcon={<PictureAsPdfIcon />}
              onClick={handleVisualizarIsencao}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Visualizar Isenção
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: 3 }} />

        {isencao && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Empreendimento
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {isencao.empreendimentoNome || "—"}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Status
            </Typography>
            <Typography variant="body1">
              {statusIsencaoLabels[isencao.status] ?? isencao.status}
            </Typography>
          </Box>
        )}

        {!temDocumentoEmitido ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Nenhum documento emitido encontrado. Aguarde o analista emitir a
            isenção antes de assinar.
          </Alert>
        ) : jaAssinada ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Esta isenção já foi assinada digitalmente.
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              Faça o upload do seu certificado digital (.pfx) e informe a senha
              para assinar a isenção emitida.
            </Alert>

            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mb: 2 }}
            >
              {certificadoPfxFile
                ? `Certificado: ${certificadoPfxFile.name}`
                : "Selecionar Certificado Digital (.pfx)"}
              <input
                type="file"
                accept=".pfx"
                hidden
                onChange={handlePfxChange}
              />
            </Button>

            <TextField
              fullWidth
              label="Senha do Certificado"
              type={mostrarSenha ? "text" : "password"}
              value={certificadoSenha}
              onChange={(e) => setCertificadoSenha(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setMostrarSenha(!mostrarSenha)}
                      aria-label={
                        mostrarSenha ? "Ocultar senha" : "Mostrar senha"
                      }
                    >
                      {mostrarSenha ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Isenção assinada com sucesso! Redirecionando...
              </Alert>
            )}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="flex-end"
              sx={{ alignItems: "stretch" }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate("/secretario/dashboard")}
                fullWidth
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAssinar}
                disabled={signing}
                fullWidth
                startIcon={signing ? <CircularProgress size={16} /> : null}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {signing ? "Assinando..." : "Assinar Isenção"}
              </Button>
            </Stack>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default AssinarIsencao;
